import React, { useState, useCallback, useEffect } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';

import './styles.less';

export const SkewedEdgeContainer = ( props ) => {
    const [ ref, setRef ] = useState( null );
    const [ containerSize, setContainerSize ] = useState( undefined );

    const updateContainerSize = useCallback( () => {
        const containerSize = ( ref ) ? ref.getBoundingClientRect() : undefined;
        setContainerSize( containerSize );
    }, [ ref ] );

    const createEdgeProps = useCallback( ( side, offset, ascendClockwise ) => {
        // Verify container data.
        if ( !containerSize ) {
            return {};
        }

        // Calculate the offset.
        let calculatedOffset = 0;
        const isFirstOffsetPercentage = offset.charAt( offset.length - 1 ) === '%';
        if ( isFirstOffsetPercentage ) {
            const numberStr = offset.splice( 0, offset.length - 1 );
            calculatedOffset = parseFloat( numberStr ) * ( side === 'left' || side === 'right' )
                ? containerSize.width
                : containerSize.height;
            calculatedOffset += 'px'; 
        } else {
            calculatedOffset = offset;
        }
            
        switch ( side ) {
            case 'top': {
                return ascendClockwise ? {
                    borderWidth: `${ calculatedOffset } ${ containerSize.width }px 0 0`, // top right bottom left
                    borderColor: `transparent ${ props.color } transparent transparent`, // top right bottom left
                    borderStyle: 'solid',
                } : {
                    borderWidth: `${ calculatedOffset } 0 0 ${ containerSize.width }px`, // top right bottom left
                    borderColor: `transparent transparent transparent ${ props.color }`, // top right bottom left
                    borderStyle: 'solid',
                };
            }
            case 'bottom': {
                return ascendClockwise ? {
                    borderWidth: `0 0 ${ calculatedOffset } ${ containerSize.width }px`, // top right bottom left
                    borderColor: `transparent transparent transparent ${ props.color }`, // top right bottom left
                    borderStyle: 'solid',
                } : {
                    borderWidth: `0 ${ containerSize.width }px ${ calculatedOffset } 0`, // top right bottom left
                    borderColor: `transparent ${ props.color } transparent transparent`, // top right bottom left
                    borderStyle: 'solid',
                };
            }
            case 'left': {
                return ascendClockwise ? {
                    borderWidth: `${ containerSize.height }px 0 0 ${ calculatedOffset }`, // top right bottom left
                    borderColor: `${ props.color } transparent transparent transparent`, // top right bottom left
                    borderStyle: 'solid',
                } : {
                    borderWidth: `${ containerSize.height }px ${ calculatedOffset } 0 0`, // top right bottom left
                    borderColor: `transparent ${ props.color } transparent transparent`, // top right bottom left
                    borderStyle: 'solid',
                };
            }
            case 'right': {
                return ascendClockwise ? {
                    borderWidth: `${ containerSize.height }px 0 0 ${ calculatedOffset }`, // top right bottom left
                    borderColor: `transparent transparent transparent ${ props.color }`, // top right bottom left
                    borderStyle: 'solid',
                } : {
                    borderWidth: `${ containerSize.height }px ${ calculatedOffset } 0 0`, // top right bottom left
                    borderColor: `${ props.color } transparent transparent transparent`, // top right bottom left
                    borderStyle: 'solid',
                };
            }
            default: {
                return {};
            }
        }
    }, [ containerSize, props.color ] );

    useEffect( () => {
        // Update once at start.
        updateContainerSize();

        if ( props.updateOnWindowResize ) {
            window.addEventListener( 'resize', updateContainerSize );
        }
        return () => {
            if ( props.updateOnWindowResize ) {
                window.removeEventListener( 'resize', updateContainerSize );
            }
        };
    }, [ props.updateOnWindowResize, updateContainerSize ] );

    // Construct the classes to use for the root component.
    const classes = classnames(
        'skewed-edge-container',
        { [ props.className ]: Boolean( props.className ) },
    );

    return (
        <div ref={ setRef } className={ classes } style={ props.style }>
            { props.top && // Top Edge
                <div
                    className={ `skewed-edge top ${ props.ignoreSkewedEdges ? 'ignore' : '' }` }
                    style={ createEdgeProps( 'top', props.offset, props.ascendClockwise ) }
                />
            }
            <div className="skewed-edge-container-children">
                { props.children }
            </div>
            { props.bottom && // Bottom Edge
                <div
                    className={ `skewed-edge bottom ${ props.ignoreSkewedEdges ? 'ignore' : '' }` }
                    style={ createEdgeProps( 'bottom', props.offset, props.ascendClockwise ) }
                />
            }
        </div>
    );
};

SkewedEdgeContainer.defaultProps = {
    ignoreSkewedEdges: false,
    updateOnWindowResize: true,
    color: 'red',
    offset: '50px',
    ascendClockwise: true,
    top: false,
    left: false,
    bottom: false,
    right: false,
};

SkewedEdgeContainer.propTypes = {
    className: PropTypes.string,
    style: PropTypes.object,
    ignoreSkewedEdges: PropTypes.bool,
    updateOnWindowResize: PropTypes.bool,
    color: PropTypes.string,
    offset: PropTypes.oneOfType( [ PropTypes.string, PropTypes.number ] ),
    ascendClockwise: PropTypes.bool,
    top: PropTypes.bool,
    bottom: PropTypes.bool,
};
